(ns client-portal.client.oasys.sessions.filters.views
  (:require [clojure.string :as s]
            [client-portal.client.oasys.sessions.filters.data :as oasys.filters.d]
            [client-portal.client.smart-ordering.filters.views :as so.filters]
            [client-portal.client.vi.components.tag :as tag]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.components.components :as c]
            [shared.core.constants :as constants]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(defn by->text [by]
  (some #(when (= (:value %) by) (:text %)) oasys.filters.d/filter-by-options))

(defn value-label [by {:strs [name value]}]
  (if (= by "metrics")
    (str (tag/metric-code->display-name name) ": " value)
    name))

(defn filter-note [text]
  [:span {:style {:color (style/alc-color "Text/secondary")
                  :font-size 12
                  :line-height 1.33}}
   text])

(defn metric-values
  "The metric picker and the values it recorded, for a Metrics filter. Values are kept
   as {\"name\" metric-key \"value\" v} pairs, the shape the Insights tag filter stores;
   the server expands a key to every metric code spelled to it."
  [{:keys [index metric metrics filter-values]}]
  (if (empty? metrics)
    [filter-note "No metrics recorded in the last 90 days."]
    [:div {:style {:display :flex
                   :flex-wrap :wrap
                   :align-items :center
                   :gap 6}}
     [:div {:style {:flex "1 0 190px"}}
      [alc/up-dropdown {:placeholder "Metric"
                        :value metric
                        :on-change #(re/dispatch [:oasys-filters/select-metric index %])
                        :options (mapv (fn [{:keys [key]}]
                                         {:value key
                                          :text (tag/metric-code->display-name key)})
                                       metrics)}]]
     (when metric
       [:div {:style {:flex "1 1 220px"}}
        (if (not-empty filter-values)
          [alc/up-multiple-dropdown {:placeholder "Select Values"
                                     :form-path (conj oasys.filters.d/filter-data-path index)
                                     :form-name :value
                                     :options (mapv (fn [v]
                                                      {:value {"name" metric "value" v}
                                                       :text v})
                                                    filter-values)}]
          [filter-note "No values recorded for this metric."])])]))

(defn topic-child [{:keys [index child-idx child]}]
  [:div {:style {:display :flex
                 :align-items :center
                 :gap 8}}
   [:div {:style {:display :flex
                  :flex-wrap :wrap
                  :align-items :center
                  :gap 6
                  :flex 1}}
    [:div {:style {:flex "1 0 170px"}}
     [alc/up-dropdown {:placeholder "Child"
                       :value (:by child)
                       :on-change #(re/dispatch [:oasys-filters/update-child index child-idx :by %])
                       :options oasys.filters.d/topic-child-options}]]
    [:div {:style {:flex "1 0 110px"}}
     [alc/up-dropdown {:placeholder "Condition"
                       :value (:condition child)
                       :on-change #(re/dispatch [:oasys-filters/update-child index child-idx :condition %])
                       :options [{:value "include" :text "Includes"}
                                 {:value "exclude" :text "Excludes"}]}]]
    [:div {:style {:flex "1 0 100px"}}
     [alc/up-dropdown {:placeholder "Any Of"
                       :value (:join child)
                       :on-change #(re/dispatch [:oasys-filters/update-child index child-idx :join %])
                       :options [{:value "or" :text "Any Of"}]}]]
    [:div {:style {:flex "1 1 160px"}}
     [alc/up-multiple-dropdown {:placeholder "Select Resolution"
                                :value (:value child)
                                :on-change #(re/dispatch [:oasys-filters/update-child index child-idx :value %])
                                :options (mapv #(hash-map :value % :text (util/->capitalize-words %))
                                               oasys.filters.d/resolution-statuses)}]]]
   [:div {:style {:flex "0 0 auto"
                  :display :flex
                  :align-items :center}}
    [alc/up-ghost-button {:icon-type :CrossIcon
                          :on-click #(re/dispatch [:oasys-filters/remove-child index child-idx])}]]])

(defn filter-pair [{:keys [index]}]
  (let [{:keys [by condition join value children metric]} (get @(re/subscribe [:oasys-filters/filter-data-]) index)
        admin? @(re/subscribe [:account/admin-role?-])
        catalogs {"topics" @(re/subscribe [:oasys-filters/topics-])
                  "channel" @(re/subscribe [:oasys-filters/channels-])
                  "intents" @(re/subscribe [:oasys-filters/intents-])
                  "metrics" (when admin?
                              @(re/subscribe [:oasys-filters/metrics-]))}
        catalog-name (get {"topics" "topics" "intents" "intents" "channel" "channels" "metrics" "metrics"} by)
        failed? (= :error (get catalogs by))
        listed #(let [v (util/when-not-loading (get catalogs %))]
                  (when (sequential? v) v))
        topics (listed "topics")
        intents (listed "intents")
        channels (listed "channel")
        metrics (listed "metrics")
        filter-values (some #(when (= (:key %) metric) (:filter-values %)) metrics)
        no-options? (and (not failed?)
                         (case by
                           "topics" (empty? topics)
                           "intents" (empty? intents)
                           "channel" (empty? channels)
                           "metrics" (or (empty? metrics) (and metric (empty? filter-values)))
                           false))
        failed-note [filter-note (str "Couldn't load " catalog-name ". Close and reopen the filter to try again.")]
        any-only? (contains? oasys.filters.d/any-only-bys by)
        topic-children (when (and (= by "topics") (not-empty value))
                         (or (not-empty children) [oasys.filters.d/default-topic-child]))]
    [alc/card {:title (str "Filter " (inc index))
               :title-inline-contents [[c/spacer]
                                       [alc/up-button {:icon-type :DeleteIcon
                                                       :on-click #(re/dispatch [:oasys-filters/delete-filter-pair index])}]
                                       (when topic-children
                                         [alc/up-button {:icon-type :AddIcon
                                                         :on-click #(re/dispatch [:oasys-filters/add-child index])}
                                          "Add Child"])]
               :style {:gap 8
                       :padding "8px 16px 16px"}}
     [:div {:style {:display :flex
                    :flex-wrap :wrap
                    :align-items :center
                    :gap 6}}
      [:div {:style {:flex "1 0 190px"}}
       [alc/up-dropdown {:placeholder "Filter by"
                         :value by
                         :on-change #(re/dispatch [:oasys-filters/select-by index %])
                         :options (cond->> oasys.filters.d/filter-by-options
                                    (not admin?) (remove #(= (:value %) "metrics")))}]]
      [:div {:style {:flex "1 0 110px"}}
       [alc/up-dropdown {:placeholder "Condition"
                         :value condition
                         :on-change #(re/dispatch [:oasys-filters/update-filter-pair index :condition %])
                         :options [{:value "include" :text "Includes"}
                                   {:value "exclude" :text "Excludes"}]}]]
      [:div {:style {:flex "1 0 100px"}}
       [alc/up-dropdown {:placeholder (if any-only? "Any Of" "Any / All")
                         :value join
                         :on-change #(re/dispatch [:oasys-filters/update-filter-pair index :join %])
                         :options (filterv identity
                                           [{:value "or" :text "Any Of"}
                                            (when-not any-only?
                                              {:value "and" :text "All Of"})])}]]
      (when (and by (not= by "metrics"))
        [:div {:style {:flex "1 1 160px"}}
         (cond
           failed?
           failed-note

           no-options?
           [filter-note (str "No " catalog-name " recorded in the last 90 days.")]

           :else
           [alc/up-multiple-dropdown
            {:placeholder (str "Select " (by->text by))
             :form-path (conj oasys.filters.d/filter-data-path index)
             :form-name :value
             :options (mapv (fn [option]
                              {:value (util/->kebab-case-string option)
                               :text (:name option)})
                            (case by
                              "topics" (mapv #(hash-map :name (:name %) :value (:name %)) topics)
                              "intents" (mapv #(hash-map :name (tag/intent-name->display-name (:name %)) :value (:name %)) intents)
                              "channel" (mapv #(hash-map :name (:name %) :value (:name %)) channels)
                              "resolution" (mapv #(hash-map :name (util/->capitalize-words %) :value %)
                                                 oasys.filters.d/resolution-statuses)
                              "escalation" [{:name "Escalated" :value "escalated"}
                                            {:name "Contained" :value "contained"}]
                              "day-of-week" (mapv #(hash-map :name % :value %) constants/days-of-the-week)
                              "time-of-day" (mapv #(hash-map :name %
                                                             :value (util/time-of-day-with-leading-zero %))
                                                  constants/hours-of-day)
                              "duration" so.filters/duration-options
                              []))}])])]
     (when topic-children
       (into [:div {:style {:display :flex
                            :flex-direction :column
                            :gap 8}}]
             (map-indexed (fn [child-idx child]
                            [topic-child {:index index
                                          :child-idx child-idx
                                          :child child}])
                          topic-children)))
     (when (= by "metrics")
       (if failed?
         failed-note
         [metric-values {:index index
                         :metric metric
                         :metrics metrics
                         :filter-values filter-values}]))
     (when (and by (empty? value) (not no-options?) (not failed?))
       [filter-note (if (= by "metrics")
                      "Pick a metric and at least one of its values to apply this filter."
                      "Select at least one value to apply this filter.")])]))

(defn filter-popover []
  (let [filter-data @(re/subscribe [:oasys-filters/filter-data-])]
    [:div {:style {:background-color (style/alc-color "Background/up_level")
                   :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                   :border-radius 6
                   :box-shadow (style/alc-shadow "shadow/common_overlay")
                   :z-index 49
                   :padding 12
                   :display :flex
                   :flex-direction :column
                   :gap 12
                   :width 650
                   :max-width "calc(100vw - 32px)"}}
     [:div {:style {:display :flex
                    :align-items :center
                    :justify-content :space-between}}
      [:span {:style {:font-size 20
                      :font-weight 400
                      :line-height 1.4
                      :color (style/alc-color "Text/primary_header")}}
       "Filter"]
      [alc/up-button {:icon-type :AddIcon
                      :on-click #(re/dispatch [:oasys-filters/add-filter-pair])}
       "Add New"]]
     (into [:div {:style {:display :flex
                          :flex-direction :column
                          :gap 8}}]
           (map-indexed (fn [idx _]
                          [filter-pair {:index idx}])
                        filter-data))
     [:div {:style {:display :flex
                    :align-items :center
                    :gap 10}}
      [alc/up-button {:disabled (empty? (filter :value filter-data))
                      :on-click #(re/dispatch [:oasys-filters/clear])}
       "Reset Filters"]
      [c/spacer]
      [alc/up-call-to-action-button {:disabled @(re/subscribe [:oasys-filters/unchanged?-])
                                     :on-click #(re/dispatch [:oasys-filters/apply])}
       "Apply"]]]))

(defn applied-filter-pills []
  (let [applied (filterv :value @(re/subscribe [:oasys-filters/applied-filters-]))
        condition-text #(-> % (util/pluralize 2) (util/->capitalize-words))
        join-text #(if (= % "and") "All Of" "Any Of")
        quoted #(str "\"" (s/join ", " %) "\"")]
    (when (not-empty applied)
      (into [:div {:style {:display :flex
                           :align-items :center
                           :flex-wrap :wrap
                           :gap 8}}]
            (concat
              (map-indexed
                (fn [idx {:keys [by condition join value children]}]
                  [alc/up-tag {:text (str (by->text by) " "
                                          (condition-text condition) " "
                                          (join-text join) " "
                                          (quoted (map #(value-label by %) value))
                                          (some->> (not-empty children)
                                                   (map (fn [child]
                                                          (str "\""
                                                               (some #(when (= (:value %) (:by child)) (:text %))
                                                                     oasys.filters.d/topic-child-options)
                                                               "\" "
                                                               (condition-text (:condition child)) " "
                                                               (join-text (:join child)) " "
                                                               (quoted (map util/->capitalize-words (:value child))))))
                                                   (s/join " and ")
                                                   (str " and ")))
                               :selected true
                               :on-close #(re/dispatch [:oasys-filters/remove-applied-filter idx])}])
                applied)
              [[alc/up-ghost-button {:on-click #(re/dispatch [:oasys-filters/clear])}
                "Clear Filters"]])))))

(defn filter-button []
  (let [applied- (re/subscribe [:oasys-filters/applied-filters-])
        open?- (re/subscribe [:oasys-filters/popover-open?-])
        topics- (re/subscribe [:oasys-filters/topics-])
        channels- (re/subscribe [:oasys-filters/channels-])
        metrics- (re/subscribe [:oasys-filters/metrics-])
        intents- (re/subscribe [:oasys-filters/intents-])]
    (fn []
      (let [filter-count (count (filter :value @applied-))
            open? @open?-]
        [alc/up-popover
         {:open open?
          :on-open-change #(re/dispatch [:oasys-filters/toggle-popover %])
          :modal false
          :side :bottom
          :align :end
          :side-offset 8
          :collision-padding 16
          :class-name "w-auto p-0 border-0 bg-transparent shadow-none"
          :trigger [:span {:style {:display :inline-flex}}
                    [alc/up-button (cond-> {:icon-type :FilterIcon
                                            :disabled (some util/loading? [@topics- @channels- @metrics- @intents-])}
                                           open? (assoc :variant "togglePressed"))
                     (if (pos? filter-count)
                       (str filter-count " " (util/pluralize "Filter" filter-count))
                       "Add Filter")]]
          :content [filter-popover]}]))))
